// frontend/src/components/SupplierCategoriesSettings.jsx
// Roadmap 3.6 (C5) — gestión del catálogo de categorías de proveedor en
// Ajustes: lista + añadir + renombrar inline + borrar con confirmación.
// «Otros gastos» es la fila fallback del sistema: no se renombra ni se borra
// (el backend responde 409; aquí los botones van deshabilitados con tooltip).
// Renombrar PROPAGA en el backend a los proveedores que ya usan la categoría.
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { apiFetch, API } from "@/utils/api";

const FALLBACK_TOOLTIP =
  "Categoría por defecto del sistema: aquí van los proveedores sin categoría. No se puede renombrar ni borrar.";

export default function SupplierCategoriesSettings() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchCatalog = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/supplier-categories/catalog`);
      if (res.ok) {
        setEntries(await res.json());
      } else {
        toast.error("No se pudo cargar el catálogo de categorías");
      }
    } catch (e) {
      toast.error("Error de conexión al cargar las categorías");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name || adding) return;
    setAdding(true);
    try {
      const res = await apiFetch(`${API}/supplier-categories/catalog`, {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      if (res.ok) {
        toast.success(`Categoría «${name}» añadida`);
        setNewName("");
        fetchCatalog();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo añadir la categoría");
      }
    } catch (e) {
      toast.error("Error de conexión");
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.catalog_id);
    setEditName(entry.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
  };

  const handleRename = async (entry) => {
    const name = editName.trim();
    if (savingEdit) return;
    if (!name || name === entry.name) {
      cancelEdit();
      return;
    }
    setSavingEdit(true);
    try {
      const res = await apiFetch(`${API}/supplier-categories/catalog/${entry.catalog_id}`, {
        method: "PUT",
        body: JSON.stringify({ name }),
      });
      if (res.ok) {
        toast.success(`Categoría renombrada a «${name}» (los proveedores que la usaban se han actualizado)`);
        cancelEdit();
        fetchCatalog();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo renombrar la categoría");
      }
    } catch (e) {
      toast.error("Error de conexión");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`¿Eliminar la categoría «${entry.name}»?`)) return;
    try {
      const res = await apiFetch(`${API}/supplier-categories/catalog/${entry.catalog_id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        toast.success(`Categoría «${entry.name}» eliminada`);
        fetchCatalog();
      } else {
        // 409 en uso: el detail explica cuántos proveedores la usan
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo eliminar la categoría", { duration: 8000 });
      }
    } catch (e) {
      toast.error("Error de conexión");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 max-w-3xl" data-testid="settings-bloque-categorias">
      <div>
        <h3 className="text-lg font-semibold text-slate-900">Categorías de proveedor</h3>
        <p className="text-sm text-slate-500">
          Clasifican el gasto de tus facturas recibidas y sus bolsas en el presupuesto.
          Renombrar una categoría actualiza también los proveedores que ya la usan.
        </p>
      </div>

      {/* Añadir */}
      <form
        className="flex flex-col sm:flex-row gap-2"
        onSubmit={(e) => { e.preventDefault(); handleAdd(); }}
      >
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Nueva categoría, p. ej. Marketing"
          maxLength={60}
          className="sm:max-w-xs"
          data-testid="supplier-cat-new-input"
        />
        <Button
          type="submit"
          disabled={adding || !newName.trim()}
          className="bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="supplier-cat-add-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> {adding ? "Añadiendo..." : "Añadir"}
        </Button>
      </form>

      {loading ? (
        <div className="text-center py-6 text-slate-500">Cargando...</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-6 text-slate-500">
          <p>No se pudo cargar el catálogo de categorías</p>
          <p className="text-sm">Recarga la página o revisa la conexión</p>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
          {entries.map((entry) => {
            const isEditing = editingId === entry.catalog_id;
            return (
              <div
                key={entry.catalog_id}
                className="flex items-center gap-2 px-4 py-2.5"
                data-testid={`supplier-cat-row-${entry.catalog_id}`}
              >
                {isEditing ? (
                  <>
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      maxLength={60}
                      autoFocus
                      className="h-8 max-w-xs"
                      data-testid="supplier-cat-edit-input"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); handleRename(entry); }
                        if (e.key === "Escape") cancelEdit();
                      }}
                    />
                    <Button
                      size="sm"
                      onClick={() => handleRename(entry)}
                      disabled={savingEdit || !editName.trim()}
                      className="h-8 bg-slate-900 hover:bg-slate-800 text-white"
                      data-testid="supplier-cat-save-btn"
                      aria-label="Guardar nombre"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={cancelEdit}
                      disabled={savingEdit}
                      className="h-8"
                      data-testid="supplier-cat-cancel-btn"
                      aria-label="Cancelar"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 min-w-0 truncate text-sm font-medium text-slate-900">
                      {entry.name}
                    </span>
                    {entry.is_fallback && (
                      <Badge
                        variant="secondary"
                        className="bg-slate-100 text-slate-500 border-slate-200 shrink-0"
                        title={FALLBACK_TOOLTIP}
                        data-testid="supplier-cat-fallback-badge"
                      >
                        fallback
                      </Badge>
                    )}
                    {entry.is_fallback ? (
                      // Botones deshabilitados con tooltip (el title de un botón
                      // disabled no siempre se muestra: va en el span envolvente)
                      <span className="flex gap-1 shrink-0" title={FALLBACK_TOOLTIP}>
                        <button
                          type="button"
                          disabled
                          className="p-1.5 rounded text-slate-300 cursor-not-allowed"
                          aria-label="La categoría fallback no se puede renombrar"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          disabled
                          className="p-1.5 rounded text-slate-300 cursor-not-allowed"
                          aria-label="La categoría fallback no se puede borrar"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ) : (
                      <div className="flex gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => startEdit(entry)}
                          className="p-1.5 rounded hover:bg-slate-100"
                          title="Renombrar"
                          data-testid={`supplier-cat-rename-${entry.catalog_id}`}
                        >
                          <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(entry)}
                          className="p-1.5 rounded hover:bg-red-50"
                          title="Eliminar"
                          data-testid={`supplier-cat-delete-${entry.catalog_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
